import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { Stack, Typography } from "@mui/material";
import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { CSVLink } from "react-csv";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { APP_ICONS } from "../../../assets";
import AppLoader from "../../../components/AppLoader";
import PaperContainer from "../../../components/Containers/PaperContainer";
import GlobalButton from "../../../components/CustomButton";
import AddEditDiscountRuleDialog from "../../../components/Dialog/AddEditDiscountRuleDialog";
import GlobalTable from "../../../components/Tables/GlobalTable";
import TextInput from "../../../components/TextInputs";
import useDebounce from "../../../hooks/useDebounce";
import { setDiscountRuleName } from "../../../lib/slices/searchKeysSlice";
import {
  getAllDiscountRules,
  updateDiscountRule,
} from "../../../services/discountRuleApiFn";
import { handleOpenSnackBar } from "../../../utils";

const headerData = [
  "createdAt",
  "name",
  "description",
  "minAmount",
  "discountAmount",
  "startDate",
  "endDate",
  "popularity",
  "status",
  "actions",
];

const tableRows = [
  "created_date",
  "discount_rule_name",
  "discount_rule_description",
  "min_amount",
  "discount_amount",
  "start_date",
  "end_date",
  "discount_popularity",
  "discount_status",
  "actions",
];

function DiscountRule() {
  const dispatch = useDispatch();
  const openDialog = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [allDiscountRules, setAllDiscountRules] = useState([]);
  const { t } = useTranslation(["catalogue", "common"]);
  const searchKeys = useSelector((state) => state.searchKeysReducer.searchKeys);
  const debouncedSearchTerm = useDebounce(searchKeys.discountRuleName, 1000);
  const { currentLanguage } = useSelector((state) => state.languageReducer);

  const fetchAllDiscountRules = async (search) => {
    try {
      setIsLoading(true);
      const params = {
        search: search,
      };
      const response = await getAllDiscountRules(params);
      if (response.data.status === "success") {
        let data = response?.data.data;
        setAllDiscountRules(data);
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAllDiscountRules(debouncedSearchTerm);
  }, [debouncedSearchTerm]);

  const handleEdit = (params) => {
    openDialog.current.openDialog({
      type: "edit",
      data: params.row,
      getData: fetchAllDiscountRules,
    });
  };

  const handleChangeDiscountStatus = async (event, row, key) => {
    try {
      let payload = {
        [key]: event,
      };
      setIsLoading(true);
      const response = await updateDiscountRule(row._id, payload);
      if (response.data.status === "success") {
        fetchAllDiscountRules(searchKeys.discountRuleName);
        handleOpenSnackBar("success", response.data.message);
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const prepareExportData = () => {
    return allDiscountRules.map((item) => ({
      "Created At": moment(item.createdAt).format("DD-MM-YYYY") || "-",
      "Updated At": moment(item.updatedAt).format("DD-MM-YYYY") || "-",
      Name: item?.name?.[currentLanguage] || "-",
      Description: item?.description?.[currentLanguage] || "-",
      "Min Amount": item.minAmount || "-",
      "Discount Amount": item.discountAmount || "-",
      "Start Date": moment(item.startDate).format("DD-MM-YYYY") || "-",
      "End Date": moment(item.endDate).format("DD-MM-YYYY") || "-",
      Status: item.isActive ? "Active" : "Inactive",
    }));
  };

  const renderHeaderAction = () => {
    return (
      <Stack
        gap={1}
        flexGrow={1}
        direction={"row"}
        alignItems={"center"}
        justifyContent={"flex-end"}
      >
        <GlobalButton
          variant={"contained"}
          btnText={"addNew"}
          handleClickBtn={() => {
            openDialog.current.openDialog({
              type: "add",
              data: null,
              getData: fetchAllDiscountRules,
            });
          }}
          styleProps={{
            width: "auto",
            height: 40,
          }}
        />
        <CSVLink
          data={prepareExportData()}
          filename={"discount-rules.csv"}
          className="csv-link"
          target="_blank"
        >
          <GlobalButton
            variant={"export_button"}
            btnText={"export"}
            startIcon={<img src={APP_ICONS.exportBlockIcon} width={20} />}
            styleProps={{
              width: "auto",
              height: 40,
            }}
          />
        </CSVLink>
      </Stack>
    );
  };

  const renderSearchAction = () => {
    return (
      <Stack p={2} direction={{ md: "row" }} bgcolor={"primary.light"}>
        <TextInput
          styleProps={{ width: { xs: "100%", md: "35%", xl: "30%" } }}
          InputStartIcon={<img src={APP_ICONS.searchGreyIcon} width={25} />}
          InputEndIcon={
            searchKeys.discountRuleName && (
              <CancelOutlinedIcon
                fontSize="small"
                sx={{ cursor: "pointer" }}
                onClick={() => {
                  dispatch(setDiscountRuleName(""));
                }}
              />
            )
          }
          placeholder={t("searchByName", { ns: "common" })}
          value={searchKeys.discountRuleName}
          onChange={(e) => {
            dispatch(setDiscountRuleName(e.target.value));
          }}
        />
      </Stack>
    );
  };

  const renderTable = useMemo(() => {
    return (
      <GlobalTable
        isLoading={isLoading}
        minWidth={1200}
        tableHeader={headerData}
        displayRows={tableRows}
        tableData={allDiscountRules || []}
        showActions={true}
        showEdit={true}
        handleEdit={handleEdit}
        handleChangeDiscountStatus={(e, row) =>
          handleChangeDiscountStatus(e, row, "isActive")
        }
        onPopularityChange={(e, row) =>
          handleChangeDiscountStatus(e, row, "isPopular")
        }
      />
    );
  }, [allDiscountRules, isLoading]);

  return (
    <React.Fragment>
      <PaperContainer
        title={t("discountRulesList")}
        subtitle={t("discountRulesDescription")}
        headerActions={renderHeaderAction()}
        // searchActions={renderSearchAction()}
      >
        {renderTable}
      </PaperContainer>

      <AddEditDiscountRuleDialog ref={openDialog} />
    </React.Fragment>
  );
}

export default DiscountRule;
